import { HttpClient } from '@angular/common/http';
import { defer, MonoTypeOperatorFunction, Observable, repeat, retry, takeUntil, tap, timer } from 'rxjs';
import { environment } from '@env/environment';
import { LiveStatus } from '@shared/models';
import { SseEvent, sseEvents } from './sse-events';

const RECONNECT_EVERY = 30 * 60_000;
const RETRY_DELAY = 5_000;

/** One topic's Mercure SSE stream over HttpClient — transport rationale in .claude/notes/live-activity-feed.md. */
export function mercureStream(httpClient: HttpClient, topic: string, lastEventId: string | null): Observable<SseEvent> {
    const params: Record<string, string> = { topic };
    if (lastEventId) {
        params['lastEventID'] = lastEventId;
    }

    // the hub authenticates by cookie only; the X-Token-Part header fails its CORS preflight
    return httpClient
        .skipApiPrefix()
        .skipTokenReadBInterceptor()
        .skipErrorHandler()
        .skipErrorMessageInterceptor()
        .get(`${environment.serverUrl}/.well-known/mercure`, {
            params,
            headers: { Accept: 'text/event-stream' },
            observe: 'events',
            responseType: 'text',
            reportDownloadProgress: true,
        })
        .pipe(sseEvents());
}

/** Re-opens `open()` every 30 min to bound sseEvents()'s cumulative partialText buffer; retries after 5 s. */
export function reconnecting<T>(open: () => Observable<T>): Observable<T> {
    return defer(open).pipe(takeUntil(timer(RECONNECT_EVERY)), repeat(), retry({ delay: () => timer(RETRY_DELAY) }));
}

/** Live once subscribed, reconnecting after an error until reconnecting() reopens it. */
export function liveStatus<T>(set: (live: LiveStatus) => void): MonoTypeOperatorFunction<T> {
    return tap({ subscribe: () => set('live'), error: () => set('reconnecting') });
}
